/** Chamadas do cadastro de gestante e parceiro (os nomes dos campos vêm da API em snake_case). */
import type {
  DadosGestacao,
  DadosGestante,
  DadosParceiro,
  ESCOLARIDADE_SINAN,
  RACAS_COR,
  RESPOSTAS_SINAN,
  RISCOS_GESTACIONAIS,
  SEXOS,
  STATUS_GESTACAO,
  ZONAS,
} from '@metaepi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enviar, requisitar } from '../../lib/api';

type Zona = (typeof ZONAS)[number];
type RacaCor = (typeof RACAS_COR)[number];
type RespostaSinan = (typeof RESPOSTAS_SINAN)[number];

/** Endereço e dados sociodemográficos no padrão SINAN (gestante e parceiro). */
export interface DadosSinanApi {
  nome_social: string | null;
  nome_mae: string | null;
  raca_cor: RacaCor | null;
  escolaridade_sinan: (typeof ESCOLARIDADE_SINAN)[number]['codigo'] | null;
  ocupacao_cbo: string | null;
  telefone: string | null;
  celular: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  ponto_referencia: string | null;
  distrito: string | null;
  zona: Zona | null;
  municipio_residencia_ibge: string | null;
}

export interface Gestante extends DadosSinanApi {
  id: string;
  nome: string;
  data_nascimento: string;
  cpf: string | null;
  cns: string | null;
  email: string | null;
  unidade_saude_id: string | null;
  unidade_nome: string | null;
  municipio_residencia_nome: string | null;
  municipio_residencia_uf: string | null;
}

export interface Parceiro extends DadosSinanApi {
  id: string;
  gestacao_id: string | null;
  nome: string | null;
  data_nascimento: string | null;
  cpf: string | null;
  cns: string | null;
  sexo: (typeof SEXOS)[number] | null;
  antecedente_sifilis: RespostaSinan | null;
  antecedente_sifilis_tratado: RespostaSinan | null;
  observacoes: string | null;
}

export interface Gestacao {
  id: string;
  unidade_saude_id: string;
  unidade_nome: string | null;
  data_ultima_menstruacao: string | null;
  data_provavel_parto: string | null;
  data_inicio_pre_natal: string | null;
  idade_gestacional_inicio_semanas: number | null;
  numero_sisprenatal: string | null;
  status: (typeof STATUS_GESTACAO)[number];
  risco: (typeof RISCOS_GESTACIONAIS)[number];
  gravidez_planejada: boolean | null;
  gestacao_multipla: boolean | null;
  observacoes: string | null;
  created_by: string | null;
  autor_nome: string | null;
  created_at: string;
}

export interface ResultadoBusca {
  id: string;
  nome: string;
  nome_social: string | null;
  data_nascimento: string;
  cpf_mascarado: string | null;
  /** Módulo 7: a criança em seguimento também é paciente (D-040). */
  tipo_paciente?: 'gestante' | 'crianca';
  nome_mae?: string | null;
  unidade_nome: string | null;
  gestacao: {
    status: Gestacao['status'];
    data_ultima_menstruacao: string | null;
    data_provavel_parto: string | null;
  } | null;
}

export interface UnidadeRede {
  id: string;
  nome: string;
  tipo_unidade?: string;
  municipio_nome: string;
  uf: string;
}

export interface MunicipioIbge {
  codigo_ibge: string;
  nome: string;
  uf: string;
}

export interface PossivelDuplicado {
  id: string;
  nome: string;
  data_nascimento: string;
  motivo: 'cpf' | 'cns' | 'nome_nascimento';
}

const CHAVE = 'pacientes';

export const useBuscaGestante = (termo: string | null) =>
  useQuery({
    queryKey: [CHAVE, 'busca', termo],
    // POST: o termo pode ser um CPF e não deve ir para a URL
    queryFn: () =>
      enviar<{ itens: ResultadoBusca[]; mais: boolean }>('/pacientes/busca', 'POST', { termo }),
    enabled: termo !== null,
    staleTime: 0,
  });

export interface EmAcompanhamento {
  itens: ResultadoBusca[];
  total: number;
  pagina: number;
  porPagina: number;
  precisaMunicipio: boolean;
}

/** Gestantes com gestação em andamento no município (médico, enfermeiro e gestor). */
export const useEmAcompanhamento = (pagina: number, ativo: boolean) =>
  useQuery({
    queryKey: [CHAVE, 'em-acompanhamento', pagina],
    queryFn: () => requisitar<EmAcompanhamento>(`/pacientes/em-acompanhamento?pagina=${pagina}`),
    enabled: ativo,
  });

export const useGestante = (id: string | undefined) =>
  useQuery({
    queryKey: [CHAVE, 'ficha', id],
    queryFn: () => requisitar<Gestante>(`/pacientes/${id}`),
    enabled: Boolean(id),
  });

export const useGestacoes = (id: string | undefined, habilitado: boolean) =>
  useQuery({
    queryKey: [CHAVE, 'gestacoes', id],
    queryFn: () =>
      requisitar<{ itens: Gestacao[] }>(`/pacientes/${id}/gestacoes`).then((r) => r.itens),
    enabled: Boolean(id) && habilitado,
  });

export const useParceiro = (id: string | undefined) =>
  useQuery({
    queryKey: [CHAVE, 'parceiro', id],
    queryFn: () =>
      requisitar<{ parceiro: Parceiro | null; existeAnterior: boolean }>(
        `/pacientes/${id}/parceiro`,
      ),
    enabled: Boolean(id),
  });

export const useUnidadesRede = () =>
  useQuery({
    queryKey: [CHAVE, 'unidades'],
    queryFn: () => requisitar<{ itens: UnidadeRede[] }>('/cadastro/unidades').then((r) => r.itens),
    staleTime: 5 * 60_000,
  });

export const useMunicipiosIbge = (busca: string) =>
  useQuery({
    queryKey: [CHAVE, 'municipios', busca],
    queryFn: () =>
      requisitar<{ itens: MunicipioIbge[] }>(
        `/cadastro/municipios?${new URLSearchParams({ busca })}`,
      ).then((r) => r.itens),
    enabled: busca.trim().length >= 2,
    staleTime: 5 * 60_000,
  });

export const verificarDuplicidade = (dados: {
  cpf?: string | null;
  cns?: string | null;
  nome?: string;
  dataNascimento?: string;
  ignorarId?: string;
}) =>
  enviar<{ itens: PossivelDuplicado[] }>('/pacientes/duplicidade', 'POST', dados).then(
    (r) => r.itens,
  );

function useMutacao<E, R>(executar: (entrada: E) => Promise<R>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: executar,
    onSuccess: () => cliente.invalidateQueries({ queryKey: [CHAVE] }),
  });
}

export const useSalvarGestante = () =>
  useMutacao(({ id, dados }: { id?: string; dados: DadosGestante }) =>
    id
      ? enviar<Gestante>(`/pacientes/${id}`, 'PUT', dados)
      : enviar<{ id: string }>('/pacientes', 'POST', dados),
  );

export const useInativarGestante = () =>
  useMutacao(({ id, motivo }: { id: string; motivo: string }) =>
    enviar<unknown>(`/pacientes/${id}/inativar`, 'POST', { motivo }),
  );

export const useSalvarGestacao = () =>
  useMutacao(
    ({
      pacienteId,
      id,
      dados,
      reaproveitarParceiro,
    }: {
      pacienteId: string;
      id?: string;
      dados: DadosGestacao;
      reaproveitarParceiro?: boolean;
    }) =>
      id
        ? enviar<{ id: string }>(`/gestacoes/${id}`, 'PUT', dados)
        : enviar<{ id: string }>(`/pacientes/${pacienteId}/gestacoes`, 'POST', {
            ...dados,
            reaproveitarParceiro,
          }),
  );

export const useSalvarParceiro = () =>
  useMutacao(
    ({
      pacienteId,
      dados,
    }: {
      pacienteId: string;
      dados: DadosParceiro | { reaproveitarAnterior: true };
    }) => enviar<Parceiro>(`/pacientes/${pacienteId}/parceiro`, 'PUT', dados),
  );
